import * as React from "react";
import { useOnline } from "@shared/lib/pwa";
import { Topbar } from "./Topbar";
import { Sidebar } from "./Sidebar";

import { cn } from "@ui/core";
import { RbacProvider, type User } from "@shared/rbac/guards";
import { useAuth } from "@app/providers/auth";
import { getTenantId as readTenantId } from "@shared/lib/auth";
import { useTenant } from "@app/providers/tenant";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@app/providers/toast";
import { useTranslation } from "react-i18next";
import { createAdminClient } from "@sdk/core";
import { ExternalLink } from "lucide-react";

import {
  buildNavigation,
  isNavigationPageReleased,
} from "@shared/config/navigation";
import { loadNavigation, navigationQueryKey } from "@shared/lib/api/navigation";
import { NavigationReleaseContext } from "@shared/lib/navigationRelease";
import {
  AUTH_ROUTE_PREFIXES,
  createLoginRedirectSearch,
  isAuthRoutePath,
} from "@app/router/routeGuards";

type AuthLogoutReason =
  | "session-expired"
  | "idle-timeout"
  | "manual"
  | "unknown";
type AuthLogoutEventDetail = { reason?: AuthLogoutReason };

import { adminHttp } from "@shared/lib/http";
import { useStorefront } from "@shared/hooks/useStorefront";
import { isFullyQualifiedHttpUrl } from "@shared/lib/storefront";
import { useKeyboardShortcuts } from "@shared/hooks/useKeyboardShortcuts";
import { ShortcutHelpModal } from "./ShortcutHelpModal";

const SIDEBAR_STORAGE_KEY = "storizone:sidebar:open";
const SIDEBAR_STORAGE_VERSION = 2;
const DESKTOP_SIDEBAR_MEDIA_QUERY = "(min-width: 1024px)";

function readStoredSidebarOpen(defaultValue = true) {
  if (typeof window === "undefined") return defaultValue;
  try {
    const raw = window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (!raw) return defaultValue;
    if (raw === "true") return true;
    if (raw === "false") return defaultValue;

    const stored = JSON.parse(raw) as {
      version?: number;
      open?: unknown;
    } | null;
    if (
      stored?.version === SIDEBAR_STORAGE_VERSION &&
      typeof stored.open === "boolean"
    ) {
      return stored.open;
    }
  } catch {}
  return defaultValue;
}

function writeStoredSidebarOpen(open: boolean) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      SIDEBAR_STORAGE_KEY,
      JSON.stringify({
        version: SIDEBAR_STORAGE_VERSION,
        open,
        updatedAt: Date.now(),
      }),
    );
  } catch {}
}

function getInitialIsLgUp() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function")
    return false;
  return window.matchMedia(DESKTOP_SIDEBAR_MEDIA_QUERY).matches;
}

function getInitialSidebarOpen() {
  return getInitialIsLgUp() ? readStoredSidebarOpen(true) : false;
}

export function Shell({
  children,
  user,
}: {
  children: React.ReactNode;
  user?: User | null;
}) {
  const [isLgUp, setIsLgUp] = React.useState(getInitialIsLgUp);
  const [sidebarOpen, setSidebarOpen] = React.useState(getInitialSidebarOpen);
  const auth = useAuth();
  const online = useOnline();
  const { tenantId } = useTenant();
  const token = auth.getToken();
  const hasRecoverableSession = Boolean(
    token || auth.refreshToken || (!online && auth.accessToken),
  );
  const queryClient = useQueryClient();

  const {
    storeSlug,
    getOpenStoreTarget,
    openStorefrontSafely,
    getStorefrontHomeUrl,
  } = useStorefront();

  const [helpOpen, setHelpOpen] = React.useState(false);

  useKeyboardShortcuts([
    {
      key: "/",
      ctrl: true,
      action: () => setHelpOpen(true),
    },
    {
      key: "k",
      ctrl: true,
      action: () => {
        const trigger = document.querySelector(
          '[data-testid="global-search-trigger"]',
        ) as HTMLButtonElement;
        if (trigger) trigger.click();
      },
    },
  ]);

  const { location } = useRouterState({
    select: (s) => ({ location: s.location }),
  });
  const isAuthRoute = AUTH_ROUTE_PREFIXES.some((path) =>
    location.pathname.startsWith(path),
  );
  const isPosRoute =
    location.pathname === "/pos" || location.pathname.startsWith("/pos/");
  const showChrome = !isAuthRoute && !isPosRoute && hasRecoverableSession;
  const navigationQuery = useQuery({
    queryKey: navigationQueryKey(tenantId),
    queryFn: loadNavigation,
    enabled: !isAuthRoute && hasRecoverableSession && Boolean(tenantId),
    staleTime: 60_000,
  });
  const redirectTarget = location.href ?? location.pathname;
  const { show, error, clearTransient } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lastLogoutHandledRef = React.useRef<{ reason: string; at: number }>({
    reason: "",
    at: 0,
  });
  const routeFeedbackKeyRef = React.useRef<string | null>(null);
  const handleOpenStore = React.useCallback(async () => {
    const result = await openStorefrontSafely();
    if (result.opened) return;

    error(result.message, {
      description:
        result.blockers.length > 0 ? result.blockers.join("\n") : undefined,
      duration: 7000,
      preserveOnNavigate: true,
    });
  }, [error, openStorefrontSafely]);

  const navItems = React.useMemo(() => {
    const items = buildNavigation(navigationQuery.data);

    if (
      storeSlug &&
      navigationQuery.data?.visibility?.["open-store"] !== false
    ) {
      const target = getOpenStoreTarget();
      const isBillingTarget = target.kind === "admin-billing";
      const storefrontHomeUrl =
        target.kind === "storefront" && isFullyQualifiedHttpUrl(target.url)
          ? target.url
          : getStorefrontHomeUrl();
      const canOpenDirectly = isFullyQualifiedHttpUrl(storefrontHomeUrl);

      items.splice(1, 0, {
        label: isBillingTarget
          ? t("layout.sidebar.reviewPlan", { defaultValue: "Review Plan" })
          : t("layout.sidebar.openStore", { defaultValue: "Open Store" }),
        to:
          isBillingTarget && target.url
            ? target.url
            : canOpenDirectly
              ? storefrontHomeUrl
              : undefined,
        target: isBillingTarget || canOpenDirectly ? "_blank" : undefined,
        icon: <ExternalLink className="h-4 w-4" />,
        onClick:
          isBillingTarget || canOpenDirectly
            ? undefined
            : () => {
                void handleOpenStore();
              },
      });
    }
    return items;
  }, [
    navigationQuery.data,
    storeSlug,
    getOpenStoreTarget,
    getStorefrontHomeUrl,
    handleOpenStore,
    t,
  ]);

  React.useEffect(() => {
    // ROBUST CHECK: Using both router location and window location
    const currentPath = (location.pathname || "").toLowerCase();
    const windowPath = (window.location.pathname || "").toLowerCase();

    // 1. If we have an access token or a refresh token, auth recovery is still possible.
    if (hasRecoverableSession) return;

    // 2. If we are on an auth route, do NOT redirect.
    // Check against router state
    if (isAuthRoutePath(currentPath) || currentPath === "/login") return;
    // Check against window state (fallback)
    if (
      windowPath.includes("login") ||
      windowPath.includes("register") ||
      windowPath.includes("signup")
    )
      return;

    // 3. Prevent recursion if 'returnTo' is already present
    if (window.location.search.includes("returnTo=")) return;

    // 4. Ensure target is not an auth route
    if (
      AUTH_ROUTE_PREFIXES.some((p) => redirectTarget.startsWith(p)) ||
      redirectTarget.includes("login")
    )
      return;

    navigate({
      to: "/login",
      search: createLoginRedirectSearch(
        { href: redirectTarget, pathname: location.pathname },
        "auth-required",
      ) as any,
      replace: true,
    } as any);
  }, [
    hasRecoverableSession,
    isAuthRoute,
    redirectTarget,
    navigate,
    location.pathname,
  ]);

  React.useEffect(() => {
    const routeFeedbackKey = `${location.pathname}${location.search ?? ""}`;
    if (routeFeedbackKeyRef.current === null) {
      routeFeedbackKeyRef.current = routeFeedbackKey;
      return;
    }

    if (routeFeedbackKeyRef.current !== routeFeedbackKey) {
      clearTransient?.();
      routeFeedbackKeyRef.current = routeFeedbackKey;
    }
  }, [clearTransient, location.pathname, location.search]);

  React.useEffect(() => {
    function onLogout(event: Event) {
      const detail = (event as CustomEvent<AuthLogoutEventDetail> | undefined)
        ?.detail;
      const reason = detail?.reason ?? "unknown";
      const now = Date.now();

      // Ignore repeated burst events from concurrent failed requests.
      if (
        lastLogoutHandledRef.current.reason === reason &&
        now - lastLogoutHandledRef.current.at < 1500
      ) {
        return;
      }
      lastLogoutHandledRef.current = { reason, at: now };

      const currentPath = (location.pathname || "").toLowerCase();
      if (AUTH_ROUTE_PREFIXES.some((path) => currentPath.startsWith(path))) {
        return;
      }

      try {
        void queryClient.cancelQueries();
        queryClient.clear();
      } catch {
        // ignore cache clear failures during forced sign-out
      }

      navigate({
        to: "/login",
        search: createLoginRedirectSearch(
          {
            href: location.href,
            pathname: location.pathname,
            searchStr: location.searchStr,
            hash: location.hash,
          },
          reason,
        ) as any,
        replace: true,
      } as any);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("auth:logout", onLogout);
      return () => window.removeEventListener("auth:logout", onLogout);
    }
  }, [
    navigate,
    location.pathname,
    location.href,
    location.searchStr,
    location.hash,
    queryClient,
  ]);

  // Dev: log tenant id to console when enabled
  React.useEffect(() => {
    if (!(import.meta as any).env?.VITE_DEBUG_TENANT) return;
    // Log on mount and on route change
    // eslint-disable-next-line no-console
    console.log("[Tenant]", readTenantId());
  }, [location.pathname]);

  // Route transition user timing (sampled)
  React.useEffect(() => {
    let t0 = performance.now();
    const prevPath = location.pathname;
    return () => {
      const t1 = performance.now();
      const dur = t1 - t0;
      if (Math.random() < 0.2) {
        try {
          const admin = createAdminClient(adminHttp) as any;
          admin.metrics.vitals({
            name: "route-transition",
            value: dur,
            id: `${prevPath}->${location.pathname}`,
            path: location.pathname,
            meta: { from: prevPath, to: location.pathname },
          });
        } catch {}
      }
      t0 = t1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  React.useEffect(() => {
    const minutes = Number(import.meta.env.VITE_IDLE_TIMEOUT_MINUTES || 0);
    if (!minutes || Number.isNaN(minutes) || minutes <= 0) return;
    const warnAt = Math.max(0, minutes - 1) * 60 * 1000; // warn 1 min before
    let last = Date.now();
    let warned = false;
    let timerId: number | null = null;
    function reset() {
      last = Date.now();
      warned = false;
      if (timerId) window.clearInterval(timerId);
      timerId = window.setInterval(tick, 1000) as unknown as number;
    }
    function tick() {
      const elapsed = Date.now() - last;
      const max = minutes * 60 * 1000;
      if (!warned && elapsed >= warnAt && elapsed < max) {
        warned = true;
        show(t("shell.inactiveWarning") as string);
      }
      if (elapsed >= max) {
        // trigger global logout flow
        try {
          window.dispatchEvent(
            new CustomEvent("auth:logout", {
              detail: { reason: "idle-timeout" },
            }),
          );
        } catch {}
        if (timerId) window.clearInterval(timerId);
      }
    }
    const events = ["mousemove", "keydown", "scroll", "click"];
    events.forEach((e) =>
      window.addEventListener(e, reset, { passive: true } as any),
    );
    reset();
    return () => {
      if (timerId) window.clearInterval(timerId);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [show, t]);

  // Track viewport breakpoint (md and up)
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(DESKTOP_SIDEBAR_MEDIA_QUERY);
    const update = () => {
      const nextIsLgUp = mq.matches;
      setIsLgUp(nextIsLgUp);
      setSidebarOpen(nextIsLgUp ? readStoredSidebarOpen(true) : false);
    };
    update();
    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);

  const toggleSidebar = React.useCallback(() => {
    setSidebarOpen((current) => {
      const next = !current;
      if (isLgUp) writeStoredSidebarOpen(next);
      return next;
    });
  }, [isLgUp]);

  const expandSidebar = React.useCallback(() => {
    setSidebarOpen(true);
    if (isLgUp) writeStoredSidebarOpen(true);
  }, [isLgUp]);

  const closeMobileSidebar = React.useCallback(() => {
    setSidebarOpen((current) => (isLgUp ? current : false));
  }, [isLgUp]);

  return (
    <NavigationReleaseContext.Provider
      value={isNavigationPageReleased(location.pathname, navigationQuery.data)}
    >
      <RbacProvider user={(user as any) ?? (auth.user as any) ?? null}>
        <div className="min-h-screen bg-background text-foreground">
          {showChrome ? (
            <Topbar
              isSidebarOpen={sidebarOpen}
              onToggleSidebar={toggleSidebar}
            />
          ) : null}

          <div
            className={cn(
              "w-full",
              isPosRoute
                ? "min-h-dvh"
                : "mx-auto max-w-screen-2xl px-4 py-5 md:px-5 md:py-6 lg:px-6 lg:py-7",
            )}
          >
            {showChrome && !isLgUp ? (
              <Sidebar
                items={navItems}
                isOpen={sidebarOpen}
                onClose={closeMobileSidebar}
              />
            ) : null}
            {showChrome ? (
              <div className="grid grid-cols-12 gap-5 lg:gap-6">
                {isLgUp && (
                  <div
                    className={cn(
                      "col-span-12 transition-all duration-300",
                      sidebarOpen ? "lg:col-span-2" : "lg:col-span-1",
                    )}
                  >
                    <Sidebar
                      items={navItems}
                      isOpen={sidebarOpen}
                      isCollapsed={!sidebarOpen}
                      onExpand={expandSidebar}
                      onClose={closeMobileSidebar}
                    />
                  </div>
                )}
                <main
                  key={`${tenantId ?? "no-tenant"}:${location.pathname}${location.search}`}
                  id="main-content"
                  className={cn(
                    "min-w-0 col-span-12 transition-all duration-300",
                    sidebarOpen && isLgUp
                      ? "lg:col-span-10"
                      : isLgUp
                        ? "lg:col-span-11"
                        : "lg:col-span-12",
                  )}
                >
                  {children}
                </main>
              </div>
            ) : (
              <main
                key={`${tenantId ?? "no-tenant"}:${location.pathname}${location.search}`}
                id="main-content"
                className={cn("min-w-0 col-span-12", isPosRoute && "min-h-dvh")}
              >
                {children}
              </main>
            )}
          </div>
          {!isPosRoute ? (
            <ShortcutHelpModal open={helpOpen} onOpenChange={setHelpOpen} />
          ) : null}
        </div>
      </RbacProvider>
    </NavigationReleaseContext.Provider>
  );
}
